import { useState } from 'react';
import type { TokenSchema, TextStyle, ButtonPadding, GridConfig } from '../lib/tokenSchema.ts';
import { fieldRemToPx, fieldPxToRem } from '../lib/units.ts';
import { normalizeFontStack } from '../lib/fontStack.ts';
import { btnSolid, sectionDesc } from '../lib/ui.ts';
import { FlapText } from './FlapText.tsx';

// Grid lengths are px-number anchors (config@0.8.0 derives the fluid scaling).
const DEFAULT_GRID: GridConfig = {
  columns: 12,
  gutter: 24,
  margin: 48,
  maxWidth: 2560,
  mobile: { columns: 4, gutter: 12, margin: 16 },
};

// Fallback button padding (px anchors: y = top+bottom, x = left+right) when the
// token is unset — config@0.8.0 emits --button-padding from these.
const DEFAULT_BUTTON_PADDING: ButtonPadding = { y: 12, x: 24 };

/**
 * The confirmable Brand-kit page — the plain-language replacement for the raw
 * JSON token editor. It renders the design system FROM the tokens (live palette,
 * type specimens and a working button) and lets the designer tweak the key values,
 * then lock. No jargon, no JSON. On lock the tokens are pushed to the client repo
 * (brand.ts) exactly as before, which is what Review's build chat reads.
 *
 * B1 scope: palette · typography · button (all token-backed). Grid is shown as a
 * placeholder until @alchmystudio/config's TokenSchema gains a `grid` field (a
 * client-sites contract) and the AI-extraction increment (B2) fills it.
 */

// Resolve a textStyle's CSS-var family (e.g. 'var(--font-heading)') to the real
// family name for an in-app specimen preview.
function resolveFamily(family: string, ff: TokenSchema['typography']['fontFamilies']): string {
  if (family.includes('--font-heading')) return ff.heading;
  if (family.includes('--font-accent')) return ff.accent ?? ff.heading;
  if (family.includes('--font-body')) return ff.body;
  return family;
}

type FontRole = 'heading' | 'body' | 'accent';

// #GR71 — which role a textStyle.family follows: a var(--font-<role>) reference directly,
// else a literal stack matched to a role by normalized-stack equality (the same rule the
// server normalizer uses to repoint literals). '' = a custom literal that follows no role.
function familyRole(family: string, ff: TokenSchema['typography']['fontFamilies']): FontRole | '' {
  if (family.includes('--font-heading')) return 'heading';
  if (family.includes('--font-accent')) return 'accent';
  if (family.includes('--font-body')) return 'body';
  const n = normalizeFontStack(family);
  if (n === normalizeFontStack(ff.heading)) return 'heading';
  if (n === normalizeFontStack(ff.body)) return 'body';
  if (ff.accent !== undefined && n === normalizeFontStack(ff.accent)) return 'accent';
  return '';
}

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

export function BrandKit({
  tokens,
  onChange,
  onLock,
  busy,
  locked,
  isLive,
  hideLock = false,
}: {
  tokens: TokenSchema;
  onChange: (next: TokenSchema) => void;
  onLock: () => void;
  busy: boolean;
  locked: boolean;
  isLive: boolean;
  // Phase 4 item 2: skip the trailing Lock button when the wizard surfaces its own
  // primary step-level Lock above the "Fine-tune" accordion (avoids a double button).
  hideLock?: boolean;
}) {
  const c = tokens.color;
  const ff = tokens.typography.fontFamilies;

  const setColor = (key: keyof TokenSchema['color'], value: string) => {
    const next = clone(tokens);
    (next.color as unknown as Record<string, string>)[key] = value;
    onChange(next);
  };
  const setFamily = (key: keyof TokenSchema['typography']['fontFamilies'], value: string) => {
    const next = clone(tokens);
    if (key === 'accent' && value.trim() === '') {
      // accent is optional — an empty string fails strict validation, so clear the key.
      delete next.typography.fontFamilies.accent;
    } else {
      next.typography.fontFamilies[key] = value;
    }
    onChange(next);
  };
  const setTextStyle = (key: string, patch: Partial<TextStyle>) => {
    const next = clone(tokens);
    next.typography.textStyles[key] = { ...next.typography.textStyles[key], ...patch };
    onChange(next);
  };
  const setButton = (group: 'default' | 'hover' | 'pressed', field: 'bg' | 'text' | 'border', value: string) => {
    const next = clone(tokens);
    next.button[group][field] = value;
    onChange(next);
  };
  const setRadius = (value: string) => {
    const next = clone(tokens);
    next.button.radius = value;
    onChange(next);
  };
  const setPadding = (axis: 'y' | 'x', n: number | undefined) => {
    const next = clone(tokens);
    const cur = next.button.padding ?? DEFAULT_BUTTON_PADDING;
    next.button.padding = { ...cur, [axis]: n ?? DEFAULT_BUTTON_PADDING[axis] };
    onChange(next);
  };
  const grid = tokens.grid ?? DEFAULT_GRID;
  const setGrid = (patch: Partial<GridConfig>) => {
    const next = clone(tokens);
    next.grid = { ...grid, ...patch };
    onChange(next);
  };
  const setMobileGrid = (patch: Partial<NonNullable<GridConfig['mobile']>>) => {
    const mobile: Record<string, number | undefined> = { ...(grid.mobile ?? {}), ...patch };
    // Drop cleared (undefined) optional keys so they don't serialize as `undefined`.
    for (const k of Object.keys(mobile)) if (mobile[k] === undefined) delete mobile[k];
    setGrid({ mobile });
  };

  return (
    <div className="space-y-6">
      {locked && (
        <p className="rounded border border-success bg-success-bright/10 p-2 text-sm text-success">
          ✓ Brand kit locked. You can tweak and re-lock it any time before going live.
        </p>
      )}
      {isLive && (
        <p className="rounded border border-warning-bright bg-warning-bright/10 p-2 text-sm text-warning">
          🔒 This site is live/protected — the brand kit is locked.
        </p>
      )}

      {/* ── Palette ─────────────────────────────────────────────── */}
      <Panel title="Colours" hint="The palette for the whole site.">
        <div className="flex flex-wrap gap-3">
          <Swatch label="Background" value={c.background} onChange={(v) => setColor('background', v)} />
          <Swatch label="Text" value={c.copy} onChange={(v) => setColor('copy', v)} />
          {c.copySubtle !== undefined && (
            <Swatch label="Subtle text" value={c.copySubtle} onChange={(v) => setColor('copySubtle', v)} />
          )}
          <Swatch label="Primary" value={c.primary} onChange={(v) => setColor('primary', v)} />
          {c.primaryHover !== undefined && (
            <Swatch label="Primary hover" value={c.primaryHover} onChange={(v) => setColor('primaryHover', v)} />
          )}
        </div>
      </Panel>

      {/* ── Typography ──────────────────────────────────────────── */}
      <Panel title="Fonts & type" hint="The fonts and their roles, with live samples.">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Labelled label="Heading font">
            <FontInput value={ff.heading} onChange={(v) => setFamily('heading', v)} />
          </Labelled>
          <Labelled label="Body font">
            <FontInput value={ff.body} onChange={(v) => setFamily('body', v)} />
          </Labelled>
          <Labelled label="Accent font (optional)">
            <FontInput value={ff.accent ?? ''} onChange={(v) => setFamily('accent', v)} placeholder="—" allowEmpty />
          </Labelled>
        </div>
        {/* DS-sweep (2-5): bg-ink is only a fallback — the inline style sets the client's
            real brand background/copy colours, so this specimen canvas shows the client's
            palette, not a token. */}
        <div className="mt-4 space-y-4 rounded bg-ink p-4" style={{ color: c.copy, background: c.background }}>
          {Object.entries(tokens.typography.textStyles).map(([key, ts]) => (
            <div key={key} className="space-y-1">
              <div className="text-[10px] uppercase tracking-wide opacity-50">{key}</div>
              <div
                style={{
                  fontFamily: resolveFamily(ts.family, ff),
                  fontSize: ts.size,
                  fontWeight: ts.weight as number,
                  lineHeight: ts.lineHeight as number,
                  letterSpacing: ts.letterSpacing,
                }}
              >
                The quick brown fox
              </div>
              <div className="flex flex-wrap items-center gap-2 text-[11px] opacity-70">
                <MiniField label="font">
                  <FontRoleSelect
                    value={familyRole(ts.family, ff)}
                    hasAccent={ff.accent !== undefined}
                    onChange={(role) => setTextStyle(key, { family: `var(--font-${role})` })}
                  />
                </MiniField>
                <MiniField label="size px">
                  <PxInput
                    valueRem={ts.size}
                    onChangeRem={(v) => setTextStyle(key, { size: v })}
                    mini
                  />
                </MiniField>
                <MiniField label="weight">
                  <MiniInput value={String(ts.weight)} onChange={(v) => setTextStyle(key, { weight: v })} />
                </MiniField>
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {/* ── Button ──────────────────────────────────────────────── */}
      <Panel title="Buttons" hint="Hover and press it — this is exactly how it behaves on the site.">
        <div className="flex flex-wrap items-center gap-6">
          <BrandButton button={tokens.button} />
          <div className="grid grid-cols-2 gap-2 text-xs">
            <Swatch small label="Fill" value={tokens.button.default.bg} onChange={(v) => setButton('default', 'bg', v)} />
            <Swatch small label="Label" value={tokens.button.default.text} onChange={(v) => setButton('default', 'text', v)} />
            <Swatch small label="Border" value={tokens.button.default.border ?? '#000000'} onChange={(v) => setButton('default', 'border', v)} />
            <Swatch small label="Hover fill" value={tokens.button.hover.bg} onChange={(v) => setButton('hover', 'bg', v)} />
            <Swatch small label="Hover border" value={tokens.button.hover.border ?? '#000000'} onChange={(v) => setButton('hover', 'border', v)} />
            <Swatch small label="Press fill" value={tokens.button.pressed.bg} onChange={(v) => setButton('pressed', 'bg', v)} />
            <Labelled label="Corner radius (px)">
              <PxInput valueRem={tokens.button.radius ?? '0'} onChangeRem={setRadius} />
            </Labelled>
            <Labelled label="Padding Y (px)">
              <NumInput
                value={tokens.button.padding?.y}
                onChange={(n) => setPadding('y', n)}
                placeholder={String(DEFAULT_BUTTON_PADDING.y)}
              />
            </Labelled>
            <Labelled label="Padding X (px)">
              <NumInput
                value={tokens.button.padding?.x}
                onChange={(n) => setPadding('x', n)}
                placeholder={String(DEFAULT_BUTTON_PADDING.x)}
              />
            </Labelled>
          </div>
        </div>
      </Panel>

      {/* ── Grid ────────────────────────────────────────────────── */}
      <Panel title="Layout grid" hint="Columns, gutters and margins the sections align to.">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Labelled label="Columns">
            <NumInput value={grid.columns} onChange={(n) => n !== undefined && setGrid({ columns: n })} />
          </Labelled>
          <Labelled label="Gutter (px)">
            <NumInput value={grid.gutter} onChange={(n) => n !== undefined && setGrid({ gutter: n })} />
          </Labelled>
          <Labelled label="Margin (px)">
            <NumInput value={grid.margin} onChange={(n) => n !== undefined && setGrid({ margin: n })} />
          </Labelled>
          <Labelled label="Max width (px)">
            <NumInput
              value={grid.maxWidth}
              onChange={(n) => n !== undefined && setGrid({ maxWidth: n })}
              placeholder="2560"
            />
          </Labelled>
        </div>
        {/* Live column preview */}
        <div
          className="mt-3 grid gap-1"
          style={{ gridTemplateColumns: `repeat(${Math.max(1, grid.columns)}, 1fr)` }}
        >
          {Array.from({ length: Math.max(1, grid.columns) }).map((_, i) => (
            <div key={i} className="h-8 rounded-sm bg-line" />
          ))}
        </div>
        <div className="mt-4 border-t border-line pt-3">
          <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-ink-2">Mobile (&lt;768px)</p>
          <div className="grid grid-cols-3 gap-3">
            <Labelled label="Columns">
              <NumInput
                value={grid.mobile?.columns}
                onChange={(n) => setMobileGrid({ columns: n })}
                placeholder="4"
              />
            </Labelled>
            <Labelled label="Gutter (px)">
              <NumInput
                value={grid.mobile?.gutter}
                onChange={(n) => setMobileGrid({ gutter: n })}
                placeholder="12"
              />
            </Labelled>
            <Labelled label="Margin (px)">
              <NumInput
                value={grid.mobile?.margin}
                onChange={(n) => setMobileGrid({ margin: n })}
                placeholder="16"
              />
            </Labelled>
          </div>
        </div>
        <p className="mt-2 text-[11px] text-ink-2">
          All sizes are in <span className="font-mono">px</span>. Gutter and margin are anchor values —
          the system scales them fluidly with the viewport for you. Max width caps the design at the
          27″ studio display.
        </p>
      </Panel>

      {!hideLock && (
        <button
          onClick={onLock}
          disabled={busy || isLive}
          className={btnSolid}
        >
          <FlapText>{busy ? 'Saving…' : locked ? 'Re-lock the brand kit →' : 'Lock the brand kit →'}</FlapText>
        </button>
      )}
    </div>
  );
}

// A live button rendered from the button tokens. Hover/press states + a basic
// press animation use a scoped <style> keyed to a unique class so each preview is
// self-contained and matches the house rules (UPPERCASE, sharp-ish, transition).
function BrandButton({ button }: { button: TokenSchema['button'] }) {
  const bd = (s: { border?: string }) => (s.border ? `1px solid ${s.border}` : '1px solid transparent');
  const pad = button.padding ?? DEFAULT_BUTTON_PADDING;
  const css = `
    .bk-btn{background:${button.default.bg};color:${button.default.text};border-radius:${button.radius ?? '0'};
      text-transform:uppercase;letter-spacing:0.04em;font-size:0.8rem;font-weight:600;padding:${pad.y}px ${pad.x}px;
      border:${bd(button.default)};cursor:pointer;transition:background .18s ease, border-color .18s ease, transform .08s ease;}
    .bk-btn:hover{background:${button.hover.bg};color:${button.hover.text};border:${bd(button.hover)};}
    .bk-btn:active{background:${button.pressed.bg};color:${button.pressed.text};border:${bd(button.pressed)};transform:translateY(1px) scale(0.98);}
    .bk-btn:focus-visible{outline:2px solid ${button.focus.ring};outline-offset:2px;}
  `;
  return (
    <>
      <style>{css}</style>
      <button className="bk-btn" type="button">Get in touch</button>
    </>
  );
}

function Panel({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <div className="rounded border border-line bg-paper p-4">
      <h3 className="text-sm font-semibold text-ink">{title}</h3>
      <p className={`mb-3 ${sectionDesc}`}>{hint}</p>
      {children}
    </div>
  );
}

// The native <input type="color"> rejects anything but #rrggbb; map other CSS
// colour forms to a neutral default so it never warns/blanks.
function hexForPicker(value: string): string {
  return /^#[0-9a-fA-F]{6}$/.test(value) ? value : '#000000';
}

function Swatch({
  label,
  value,
  onChange,
  small,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  small?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-ink-2">
      <span>{label}</span>
      <span className="flex items-center gap-2">
        <input
          type="color"
          // The native picker only accepts #rrggbb — coerce non-hex values
          // (e.g. 'transparent', rgb(), a CSS var) to a safe default for the
          // swatch while the text field below keeps the real value.
          value={hexForPicker(value)}
          onChange={(e) => onChange(e.target.value)}
          className={small ? 'h-6 w-6 rounded border border-line p-0' : 'h-8 w-8 rounded border border-line p-0'}
        />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
          className="w-20 rounded border border-line px-2 py-1 font-mono text-[11px]"
        />
      </span>
    </label>
  );
}

function Labelled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-xs text-ink-2">
      {label}
      {children}
    </label>
  );
}

// #GR71 — a font-family stack input. Holds the in-progress text locally and NORMALIZES
// on commit (blur/Enter) — a valid quoted stack with a fallback, matching what the server
// writes at lock — so a malformed entry ("its battilgo ") never lands. allowEmpty keeps the
// optional accent clearable (an empty commit passes '' through, which clears the key).
function FontInput({
  value,
  onChange,
  placeholder,
  allowEmpty,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  allowEmpty?: boolean;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft === null) return;
    const t = draft.trim();
    if (t === '') onChange(allowEmpty ? '' : value); // empty → clear (accent) or revert
    else onChange(normalizeFontStack(t));
    setDraft(null);
  };
  return (
    <input
      value={draft ?? value}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
      placeholder={placeholder}
      spellCheck={false}
      className="rounded border border-line px-2 py-1 text-sm"
    />
  );
}

// #GR71 — per-textStyle font picker: which ROLE this style follows. Selecting a role sets
// family = var(--font-<role>) so a later role-font swap propagates to this text. Accent is
// only offered when an accent font is defined (else var(--font-accent) would be undefined).
// A '' value = a custom literal following no role → shown as a disabled "Custom" option so
// the current state isn't misrepresented.
function FontRoleSelect({
  value,
  hasAccent,
  onChange,
}: {
  value: FontRole | '';
  hasAccent: boolean;
  onChange: (role: FontRole) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as FontRole)}
      className="rounded border border-line bg-paper px-1 py-0.5 text-[11px] text-ink-2"
    >
      {value === '' && <option value="" disabled>Custom</option>}
      <option value="heading">Heading</option>
      <option value="body">Body</option>
      {hasAccent && <option value="accent">Accent</option>}
    </select>
  );
}

// A length field shown to designers in PIXELS but stored in rem. The in-progress
// text is held in local state so the px↔rem round-trip only runs on commit
// (blur/Enter) — converting on every keystroke would fight the typist (e.g. "12"
// would reformat to "1px" after the first digit).
function PxInput({
  valueRem,
  onChangeRem,
  placeholder,
  mini,
}: {
  valueRem: string;
  onChangeRem: (rem: string) => void;
  placeholder?: string;
  mini?: boolean;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft !== null) {
      onChangeRem(fieldPxToRem(draft));
      setDraft(null);
    }
  };
  return (
    <input
      value={draft ?? fieldRemToPx(valueRem)}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
      placeholder={placeholder}
      spellCheck={false}
      className={
        mini
          ? 'w-16 rounded border border-line bg-paper px-1 py-0.5 font-mono text-[11px] text-ink-2'
          : 'rounded border border-line px-2 py-1 text-sm'
      }
    />
  );
}

// A plain whole-number (px) field — grid anchors and button padding. Holds the
// in-progress text locally and commits on blur/Enter; clearing it emits undefined
// (so optional mobile fields can be removed), an invalid entry is ignored.
function NumInput({
  value,
  onChange,
  placeholder,
}: {
  value: number | undefined;
  onChange: (n: number | undefined) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft === null) return;
    const t = draft.trim();
    if (t === '') onChange(undefined);
    else {
      const n = Number(t);
      if (!Number.isNaN(n)) onChange(n);
    }
    setDraft(null);
  };
  return (
    <input
      inputMode="numeric"
      value={draft ?? (value === undefined ? '' : String(value))}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
      placeholder={placeholder}
      spellCheck={false}
      className="rounded border border-line px-2 py-1 text-sm"
    />
  );
}

function MiniField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <span className="flex items-center gap-1">
      <span>{label}</span>
      {children}
    </span>
  );
}

function MiniInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      spellCheck={false}
      className="w-14 rounded border border-line bg-paper px-1 py-0.5 font-mono text-[11px] text-ink-2"
    />
  );
}
